import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useEffect } from 'react';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于欧美一区二区·高清内容聚合平台',
    description: '欧美一区二区是专业影视内容分区导航平台，精准划分欧美一区二区资源，提供高清流畅的分区浏览体验。',
    url: 'https://oumei-yiqierqu.com/about',
    about: {
      '@type': 'Organization',
      name: '欧美一区二区·高清内容聚合平台',
      description: '专业欧美影视内容分区导航平台',
      url: 'https://oumei-yiqierqu.com/',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-10-8888-6666',
        contactType: 'customer service',
        email: 'contact@oumei-yiqierqu.com',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
        streetAddress: '影视产业园区',
      },
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutLd)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100">
          <div className="container-main py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              关于欧美一区二区
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-2xl">
              专业欧美影视内容分区导航平台，致力于为华语观众提供最优质的欧美影视内容发现体验
            </p>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="container-main max-w-4xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    欧美一区二区诞生于对欧美影视的无限热爱与对内容导航效率的极致追求。我们的创始团队由一群深耕影视行业多年的资深从业者组成，深知在海量内容中精准定位优质资源的难度。
                  </p>
                  <p>
                    我们观察到，虽然欧美影视在中国拥有庞大的受众群体，但用户常常面临信息碎片化、分区混乱、资源难以定位等问题。于是，欧美一区二区应运而生——一个专注于欧美影视内容分区导航的专业平台。
                  </p>
                  <p>
                    从最初的手工整理到如今的系统化运营，欧美一区二区始终坚持"分区清晰、更新及时、内容精选"的核心理念，为每一位热爱欧美影视的用户提供最便捷的内容发现体验。
                  </p>
                </div>
              </div>
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20with%20warm%20ambient%20lighting%2C%20film%20posters%20on%20brick%20wall%2C%20cozy%20workspace%20with%20wooden%20desk%20and%20computer%2C%20cinematic%20atmosphere%2C%20golden%20hour%20light%20streaming%20through%20windows%2C%20professional%20yet%20artistic%20vibe%2C%20film%20production%20company%20aesthetic&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="欧美一区二区品牌故事"
                  title="欧美一区二区品牌故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="container-main max-w-4xl">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4 text-center">
              平台信誉
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10 max-w-lg mx-auto">
              数据见证我们的专业与用心
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { value: '300+', label: '覆盖影片', icon: 'ri-movie-line' },
                { value: '50万+', label: '月均访问', icon: 'ri-user-line' },
                { value: '99.7%', label: '内容准确率', icon: 'ri-shield-check-line' },
                { value: '7×24', label: '持续更新', icon: 'ri-refresh-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <span className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-lg`}></i>
                  </span>
                  <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="container-main max-w-4xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  联系方式
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮件</p>
                      <p className="text-sm text-foreground-600">contact@oumei-yiqierqu.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">联系电话</p>
                      <p className="text-sm text-foreground-600">+86 10-8888-6666</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">办公地址</p>
                      <p className="text-sm text-foreground-600">北京市朝阳区影视产业园区</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  关注我们
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <a href="#" className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-100 text-red-500">
                      <i className="ri-weibo-line text-lg"></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-700">微博</span>
                  </a>
                  <a href="#" className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-green-100 text-green-500">
                      <i className="ri-wechat-line text-lg"></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-700">微信</span>
                  </a>
                  <a href="#" className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-pink-100 text-pink-500">
                      <i className="ri-bilibili-line text-lg"></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-700">哔哩哔哩</span>
                  </a>
                  <a href="#" className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-100 text-blue-500">
                      <i className="ri-tiktok-line text-lg"></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-700">抖音</span>
                  </a>
                </div>

                <div className="mt-8 p-5 rounded-xl bg-accent-100/80 border border-accent-200/60">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    合作与投稿
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们欢迎影视行业从业者、资深影评人、内容创作者与我们合作。如果您有优质内容想分享，或有商务合作需求，请通过电子邮件与我们联系。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}